import React, { useState, useEffect } from 'react';
import {
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Chip,
  Autocomplete,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction } from
'@mui/material';
import { AttachFile, Delete } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ticketsApi } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import ResponsiveDialog from '../ResponsiveDialog';

/**
 * Enhanced dialog to save a ticket as a known issue with WYSIWYG editors
 */import { useThemeSettings } from "../../contexts/ThemeContext";
export function SaveAsKnownIssue({ ticket, open, onClose, onSaved }) {const { t } = useThemeSettings();
  const [matchingKeywords, setMatchingKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [isCreatingSolution, setIsCreatingSolution] = useState(true);
  const [complexity, setComplexity] = useState('moderate');
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);

  // Get user data from auth context
  const { user } = useAuth();
  const userID = user?.id || user?._id || null;
  const companyID = user?.companyId || user?.company_id || null;

  // Check permissions
  const { hasPermission } = usePermissions();
  const userRole = user?.role || user?.userRole || 'user';
  const isSupport = userRole === 'support' || userRole === 'admin' || hasPermission('tickets.manage');

  // Only support users and admins can create known issues
  if (!isSupport) {
    return null;
  }

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    solutionTitle: '',
    solutionDescription: '',
    solutionSteps: ''
  });

  // Reset form when ticket changes or when dialog opens
  useEffect(() => {
    if (ticket && open) {
      setFormData({
        title: ticket.title || '',
        description: ticket.description || '',
        solutionTitle: '',
        solutionDescription: ticket.steps_tried || '',
        solutionSteps: ''
      });
      setMatchingKeywords([]);
      setAttachments([]);
    }
  }, [ticket, open]);

  const complexityOptions = [
  { value: 'simple', label: 'Simple' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'complex', label: 'Complex' }];


  // Rich text editor configuration
  const quillModules = {
    toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['clean']]

  };

  const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent',
  'color', 'background', 'align',
  'link', 'image'];


  const handleAddKeyword = (event) => {
    if (event.key === 'Enter' && keywordInput.trim()) {
      setMatchingKeywords([...matchingKeywords, keywordInput.trim()]);
      setKeywordInput('');
      event.preventDefault();
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setAttachments((prev) => [...prev, ...files]);
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadAttachments = async (issueId) => {
    const uploadPromises = attachments.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('ticketId', issueId);
      formData.append('userId', userID);
      formData.append('companyId', companyID);
      if (ticket.building_id) formData.append('locationId', ticket.building_id);
      if (ticket.device_id) formData.append('deviceId', ticket.device_id);

      const response = await fetch('/api/files', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      return response.json();
    });

    return Promise.all(uploadPromises);
  };

  const handleFormSubmit = async () => {
    if (!formData.title.trim()) {
      alert('Please provide a title for the issue');
      return;
    }

    if (!formData.description.trim()) {
      alert('Please provide a description for the issue');
      return;
    }

    if (isCreatingSolution && (!formData.solutionTitle.trim() || !formData.solutionDescription.trim())) {
      alert('Please provide a title and description for the solution');
      return;
    }

    setLoading(true);
    try {
      // Save as known issue via API
      const issueData = {
        title: formData.title,
        description: formData.description,
        matchingKeywords: matchingKeywords.join(' '),
        isKnownIssue: true,
        building_id: ticket.building_id,
        room_id: ticket.room_id,
        building_service_id: ticket.building_service_id,
        // NOTE: created_by_user_id and created_by_company_id are set by the backend
        frequencyScore: 1 // Start with 1 for the current ticket
      };

      const issueResponse = await ticketsApi.createKnownIssue(issueData);
      const issueId = issueResponse?.data?.data?.id || issueResponse?.data?.id || issueResponse?.data?._id;

      //console.log('Known issue created:', { issueResponse, issueId });

      if (!issueId) {
        throw new Error('Failed to get issue ID from response');
      }

      // Upload attachments if any
      if (attachments.length > 0 && issueId) {
        try {
          await uploadAttachments(issueId);
        } catch (error) {
          console.error('Error uploading attachments:', error);
          alert('Known issue saved, but some attachments failed to upload');
        }
      }

      // Create the solution if requested
      if (isCreatingSolution && issueId) {
        const solutionData = {
          title: formData.solutionTitle,
          description: formData.solutionDescription,
          stepByStepInstructions: formData.solutionSteps,
          complexity,
          issue_id: issueId,
          known_issue_id: issueId, // Add known_issue_id for backend compatibility
          created_by_user_id: userID,
          company_id: companyID,
          status: 'validated',
          isStandardSolution: true,
          // Additional fields to ensure proper storage
          knownIssueId: issueId,
          createdByUserId: userID,
          companyId: companyID
        };

        //console.log('Creating solution with data:', solutionData);
        try {
          const solutionResponse = await ticketsApi.createSolution(solutionData);
          //console.log('Solution creation response:', solutionResponse);
        } catch (solutionError) {
          console.error('Error creating solution:', solutionError);
          console.error('Solution error details:', solutionError.response?.data);
          // Alert user but continue
          alert('Known issue saved, but failed to create solution: ' + (solutionError.response?.data?.error || solutionError.message));
        }
      }

      alert('Saved as known issue successfully');
      onSaved && onSaved(issueId);
      onClose();
    } catch (error) {
      console.error('Error saving known issue:', error);
      alert('Failed to save as known issue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t ? t("SaveAsKnownIssue.save_as_known_issue") : "Save as Known Issue"}</DialogTitle>
            <DialogContent>
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>{t ? t("SaveAsKnownIssue.issue_information") : "Issue Information"}

        </Typography>

                <TextField
          name="title"
          label={t ? t("SaveAsKnownIssue.issue_title") : "Issue Title"}
          required
          fullWidth
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          margin="normal" />
        

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>{t ? t("SaveAsKnownIssue.issue_description") : "Issue Description"}

        </Typography>

                {/* WYSIWYG Editor for Issue Description */}
                <Box sx={{ mt: 1, mb: 3, '& .ql-container': { minHeight: '200px' } }}>
                    <ReactQuill
            theme="snow"
            value={formData.description}
            onChange={(value) => handleChange('description', value)}
            modules={quillModules}
            formats={quillFormats}
            placeholder={t ? t("SaveAsKnownIssue.enter_issue_description_formatting") : "Enter issue description with formatting..."} />
          
                </Box>

                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>{t ? t("SaveAsKnownIssue.add_keywords_better_matching") : "Add Keywords for Better Matching"}

          </Typography>
                    <Autocomplete
            multiple
            freeSolo
            value={matchingKeywords}
            onChange={(event, newValue) => setMatchingKeywords(newValue)}
            options={[]}
            renderTags={(value, getTagProps) =>
            value.map((option, index) =>
            <Chip label={option} {...getTagProps({ index })} />
            )
            }
            renderInput={(params) =>
            <TextField
              {...params}
              label={t ? t("SaveAsKnownIssue.matching_keywords") : "Matching Keywords"}
              helperText={t ? t("SaveAsKnownIssue.press_enter_add_keyword") : "Press Enter to add a keyword"}
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={handleAddKeyword} />

            } />
          
                </Box>

                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>{t ? t("SaveAsKnownIssue.attachments") : "Attachments"}

          </Typography>
                    <input
            accept="*/*"
            style={{ display: 'none' }}
            id="attachment-file-input"
            type="file"
            multiple
            onChange={handleFileSelect} />
          
                    <label htmlFor="attachment-file-input">
                        <Button
              variant="outlined"
              component="span"
              startIcon={<AttachFile />}>{t ? t("SaveAsKnownIssue.add_attachments") : "Add Attachments"}


            </Button>
                    </label>
                    {attachments.length > 0 &&
          <List dense sx={{ mt: 1 }}>
                            {attachments.map((file, index) =>
            <ListItem key={index}>
                                    <ListItemText
                primary={file.name}
                secondary={`${(file.size / 1024).toFixed(2)} KB`} />
              
                                    <ListItemSecondaryAction>
                                        <IconButton
                  edge="end"
                  onClick={() => handleRemoveAttachment(index)}>
                  
                                            <Delete />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
            )}
                        </List>
          }
                </Box>

                <Box sx={{ mt: 3 }}>
                    <FormControlLabel
            control={
            <Switch
              checked={isCreatingSolution}
              onChange={(e) => setIsCreatingSolution(e.target.checked)} />

            }
            label={t ? t("SaveAsKnownIssue.add_solution_this_issue") : "Add a solution for this issue"} />
          
                </Box>

                {isCreatingSolution &&
        <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>{t ? t("SaveAsKnownIssue.solution_information") : "Solution Information"}

          </Typography>

                        <TextField
            name="solutionTitle"
            label={t ? t("SaveAsKnownIssue.solution_title") : "Solution Title"}
            required
            fullWidth
            value={formData.solutionTitle}
            onChange={(e) => handleChange('solutionTitle', e.target.value)}
            margin="normal" />
          

                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>{t ? t("SaveAsKnownIssue.solution_description") : "Solution Description"}

          </Typography>

                        {/* WYSIWYG Editor for Solution Description */}
                        <Box sx={{ mt: 1, mb: 3, '& .ql-container': { minHeight: '200px' } }}>
                            <ReactQuill
              theme="snow"
              value={formData.solutionDescription}
              onChange={(value) => handleChange('solutionDescription', value)}
              modules={quillModules}
              formats={quillFormats}
              placeholder={t ? t("SaveAsKnownIssue.enter_solution_description_formatti") : "Enter solution description with formatting..."} />
            
                        </Box>

                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>{t ? t("SaveAsKnownIssue.stepbystep_instructions") : "Step-by-Step Instructions"}

          </Typography>

                        {/* WYSIWYG Editor for Step-by-Step Instructions */}
                        <Box sx={{ mt: 1, mb: 3, '& .ql-container': { minHeight: '250px' } }}>
                            <ReactQuill
              theme="snow"
              value={formData.solutionSteps}
              onChange={(value) => handleChange('solutionSteps', value)}
              modules={quillModules}
              formats={quillFormats}
              placeholder={t ? t("SaveAsKnownIssue.enter_detailed_stepbystep_instructions_f") : "Enter detailed step-by-step instructions for resolving this issue..."} />
            
                        </Box>

                        <FormControl fullWidth margin="normal">
                            <InputLabel id="complexity-label">{t ? t("SaveAsKnownIssue.complexity") : "Complexity"}</InputLabel>
                            <Select
              labelId="complexity-label"
              value={complexity}
              label={t ? t("SaveAsKnownIssue.complexity") : "Complexity"}
              onChange={(e) => setComplexity(e.target.value)}>
              
                                {complexityOptions.map((option) =>
              <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
              )}
                            </Select>
                        </FormControl>
                    </Box>
        }
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>{t ? t("LocationDialog.cancel") : "Cancel"}

        </Button>
                <Button
          onClick={handleFormSubmit}
          variant="contained"
          color="primary"
          disabled={loading}>
          
                    {loading ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </ResponsiveDialog>);

}